'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { DragSource, DropTarget } from 'react-dnd';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import { forwardRef, useCallback, useMemo } from 'react';
import UIDragHandle from 'UIComponents/draggable/UIDragHandle';
import compose from 'transmute/compose';
import emptyFunction from 'react-utils/emptyFunction';
import get from 'transmute/get';
import memoize from 'transmute/memoize';
import UISortTH from 'UIComponents/table/UISortTH';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIIcon from 'UIComponents/icon/UIIcon';
import { EERIE, CALYPSO } from 'HubStyleTokens/colors'; // Heavily mangled from customer-data-table 's ThDragDrop

const DIRECTIONS = {
  ASC: 1,
  DESC: -1
};
const leftOrRight = memoize((columns, target, source) => {
  const sourceIndex = columns.indexOf(source);
  const targetIndex = columns.indexOf(target);
  return targetIndex < sourceIndex ? 'left' : 'right';
});
const flexStyle = {
  display: 'flex',
  flexDirection: 'row',
  height: '100%'
};
const BORDER_STYLE = `2px ${CALYPSO} solid`;
const BORDER_RIGHT = {
  borderRight: BORDER_STYLE
};
const BORDER_LEFT = {
  borderLeft: BORDER_STYLE
};

const getBorder = (columns, id, dragSourceItem) => leftOrRight(columns, id, dragSourceItem) === 'right' ? BORDER_RIGHT : BORDER_LEFT;

const ThDragDrop = /*#__PURE__*/forwardRef(({
  columns,
  editable,
  connectDragPreview,
  connectDragSource,
  connectDropTarget,
  dragSourceItem,
  id,
  isDragging,
  isOver,
  lastMouseUp,
  title,
  children = '',
  className = '',
  onSortChange,
  sortKey,
  activeSortKey,
  activeDirection,
  sortDisabled = false,
  tooltipProps = {},
  align
}, ref) => {
  const currentSort = useMemo(() => {
    if (sortKey === activeSortKey && activeDirection !== DIRECTIONS.ASC) {
      return 'descending';
    } else if (sortKey === activeSortKey && activeDirection !== DIRECTIONS.DESC) {
      return 'ascending';
    } else {
      return 'none';
    }
  }, [sortKey, activeDirection, activeSortKey]);
  const border = useMemo(() => isOver ? getBorder(columns, id, dragSourceItem) : {}, [isOver, columns, id, dragSourceItem]);
  const onSort = useCallback(() => {
    let direction = DIRECTIONS.DESC; // if we're clicking on the same key we're already sorting by we'll flip

    if (sortKey === activeSortKey) {
      direction = activeDirection * -1;
    }

    onSortChange({
      sortKey,
      direction
    });
  }, [sortKey, activeSortKey, activeDirection, onSortChange]);
  const ContentComponent = useCallback(({
    arrowsNode,
    children: thChildren
  }) => editable ? connectDropTarget( /*#__PURE__*/_jsxs("div", {
    style: Object.assign({}, flexStyle, {
      textAlign: align,
      marginLeft: align === 'right' && 'auto',
      opacity: isDragging ? 0.5 : 1,
      background: isDragging ? GREAT_WHITE : undefined,
      whiteSpace: 'nowrap'
    }),
    className: className,
    children: [thChildren, arrowsNode]
  })) : /*#__PURE__*/_jsxs("div", {
    className: className,
    style: Object.assign({}, flexStyle, {
      textAlign: align,
      marginLeft: align === 'right' && 'auto',
      whiteSpace: 'nowrap'
    }),
    children: [thChildren, arrowsNode]
  }), [className, connectDropTarget, editable, isDragging, align]);
  const THComponent = useCallback((_ref) => {
    let {
      style = {}
    } = _ref,
        props = _objectWithoutPropertiesLoose(_ref, ["hovered", "style"]);

    return editable ? connectDragPreview(connectDragSource( /*#__PURE__*/_jsx("th", Object.assign({}, props, {
      style: Object.assign({}, style, {}, border)
    })))) : /*#__PURE__*/_jsx("th", Object.assign({}, props));
  }, [editable, connectDragPreview, connectDragSource, border]);

  const sortTH = /*#__PURE__*/_jsx(UISortTH, {
    Content: ContentComponent,
    TH: THComponent,
    role: "columnheader",
    sort: currentSort,
    onSortChange: onSort,
    id: id,
    disabled: sortDisabled,
    ref: ref,
    align: align,
    children: title ? /*#__PURE__*/_jsxs("span", {
      children: [children, /*#__PURE__*/_jsx(UIIcon, {
        name: "info",
        className: "m-left-1",
        color: EERIE
      })]
    }) : children
  }, `${lastMouseUp}-${id}`);

  const wrappedSortTH = editable ? /*#__PURE__*/_jsx(UIDragHandle, {
    children: sortTH
  }) : sortTH;
  return /*#__PURE__*/_jsx(UITooltip, Object.assign({
    disabled: !title,
    title: title
  }, tooltipProps, {
    children: wrappedSortTH
  }));
});
ThDragDrop.displayName = 'ThDragDrop';
const dragSpec = {
  beginDrag(props) {
    return {
      id: props.id
    };
  },

  endDrag(props, monitor) {
    if (!monitor.didDrop()) return;
    const dropResult = monitor.getDropResult();
    const onColumnReorder = get('onColumnReorder', props) || emptyFunction;
    const sourceId = props.id;
    const targetId = dropResult.id;
    onColumnReorder(sourceId, targetId);
  }

};

const dragCollect = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging()
});

const dropSpec = {
  drop(props) {
    return {
      id: props.id
    };
  }

};

const dropCollect = (connect, monitor) => {
  const isOver = monitor.isOver();

  if (!isOver) {
    return {
      connectDropTarget: connect.dropTarget(),
      isOver
    };
  }

  return {
    connectDropTarget: connect.dropTarget(),
    isOver,
    dragSourceItem: get('id', monitor.getItem())
  };
};

export default compose(DragSource('column', dragSpec, dragCollect), DropTarget('column', dropSpec, dropCollect))(ThDragDrop);